import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import StudentAvatar from "@/components/StudentAvatar";
import { CalendarIcon, ClipboardList, Compass, ChevronDown, Users, ListChecks, MapPin } from "lucide-react";
import { fmtDate } from "@/lib/format";

const KIND_LABELS = { ministeriale: "Ministeriale", visita: "Visita", uscita: "Uscita", permesso: "Permesso", altro: "Altro" };
const KIND_COLORS = { ministeriale: "#5B7FE2", visita: "#7C3AED", uscita: "#E2725B", permesso: "#F59E0B", altro: "#556B2F" };

function Group({ members, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone-500 mb-2"><Users size={12}/> Il tuo gruppo</div>
      {members.length === 0 ? (
        <div className="text-xs text-stone-400">Sei l'unico assegnato in questa fascia.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-2 rounded-full bg-stone-50 border border-stone-200 pl-1 pr-3 py-1" data-testid={`${testId}-member-${m.id}`}>
              <StudentAvatar studentId={m.id} photoPath={m.photo_v} name={m.name} className="w-7 h-7 rounded-full text-[10px]"/>
              <span className="text-xs text-stone-700">{m.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityRow({ id, color, title, subtitle, tag, tagClass, details, members }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`my-${id}`}>
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center gap-3 text-sm text-left hover:bg-stone-50/60 transition-colors duration-150" data-testid={`my-${id}-toggle`}>
        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: color }}/>
        <div className="flex-1 min-w-0">
          <div className="font-medium">{title}</div>
          <div className="text-[11px] text-stone-500">{subtitle}</div>
        </div>
        {members.length > 0 && <span className="text-[11px] text-stone-500 inline-flex items-center gap-1"><Users size={12}/>{members.length + 1}</span>}
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${tagClass}`}>{tag}</span>
        <ChevronDown size={16} className={`text-stone-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="px-4 pb-4 pl-10 space-y-4" data-testid={`my-${id}-details`}>
          {details}
          <Group members={members} testId={`my-${id}-group`}/>
        </div>
      )}
    </div>
  );
}

function TaskText({ text, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone-500 mb-1.5"><ListChecks size={12}/> Cosa fare</div>
      <div className="text-sm text-stone-700 whitespace-pre-line leading-relaxed">{text || "Nessun dettaglio inserito dalla segreteria."}</div>
    </div>
  );
}

const timeOf = (x) => `${x.start_time || "tutto il giorno"}${x.end_time ? ` – ${x.end_time}` : ""}`;

export default function PortaleTurni() {
  const { data = { turni: [], impegni: [] } } = useQuery({
    queryKey: ["mie-attivita"],
    queryFn: async () => (await api.get("/mansioni-area/mie-attivita")).data,
  });

  const grouped = {};
  data.turni.forEach((t) => { (grouped[t.date] ||= { turni: [], impegni: [] }).turni.push(t); });
  data.impegni.forEach((e) => { (grouped[e.date] ||= { turni: [], impegni: [] }).impegni.push(e); });
  const today = new Date().toLocaleDateString("sv-SE");
  // Oggi in cima, poi i prossimi giorni, poi i giorni passati dal più recente
  const days = Object.entries(grouped).sort(([a], [b]) => {
    const ga = a === today ? 0 : a > today ? 1 : 2;
    const gb = b === today ? 0 : b > today ? 1 : 2;
    if (ga !== gb) return ga - gb;
    return ga === 2 ? b.localeCompare(a) : a.localeCompare(b);
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto">
      <PageHeader title="I miei turni e impegni" subtitle="Tocca un turno o un impegno per vedere cosa fare e chi c'è nel tuo gruppo."/>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Card className="p-4 border border-emerald-200 bg-emerald-50/40 rounded-xl shadow-none">
          <div className="flex items-center gap-2 text-emerald-900"><ClipboardList size={14}/> Turni</div>
          <div className="text-2xl font-semibold mt-1" data-testid="portale-turni-count">{data.turni.length}</div>
        </Card>
        <Card className="p-4 border border-sky-200 bg-sky-50/40 rounded-xl shadow-none">
          <div className="flex items-center gap-2 text-sky-900"><Compass size={14}/> Impegni</div>
          <div className="text-2xl font-semibold mt-1" data-testid="portale-impegni-count">{data.impegni.length}</div>
        </Card>
      </div>

      {days.length === 0 && <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">Nessuna attività assegnata.</Card>}
      <div className="space-y-3">
        {days.map(([day, { turni, impegni }]) => (
          <Card key={day} data-testid={`portale-day-${day}`} className={`border rounded-xl shadow-none overflow-hidden ${day === today ? "border-[#1C352D] ring-1 ring-[#1C352D]/20" : day < today ? "border-stone-200 opacity-70" : "border-stone-200"}`}>
            <div className={`px-4 py-2 border-b border-stone-100 flex items-center gap-2 text-sm font-medium ${day === today ? "bg-[#1C352D] text-white" : "bg-stone-50"}`}>
              <CalendarIcon size={14}/> {fmtDate(day)}
              {day === today && <span className="ml-auto text-[10px] uppercase tracking-widest" data-testid="portale-today-badge">Oggi</span>}
              {day < today && <span className="ml-auto text-[10px] uppercase tracking-widest text-stone-400">Passato</span>}
            </div>
            <div className="divide-y divide-stone-100">
              {turni.map((t) => (
                <ActivityRow key={t.id} id={`turno-${t.id}`} color={t.mansione_color || "#556B2F"} title={t.mansione_name}
                  subtitle={`${timeOf(t)}${t.note ? ` · ${t.note}` : ""}`} tag="Turno" tagClass="bg-emerald-50 text-emerald-800 border-emerald-200"
                  members={t.group || []} details={<TaskText text={t.mansione_description} testId={`my-turno-${t.id}-tasks`}/>}/>
              ))}
              {impegni.map((i) => (
                <ActivityRow key={i.id} id={`impegno-${i.id}`} color={KIND_COLORS[i.kind]} title={`${i.title} · ${KIND_LABELS[i.kind] || ""}`}
                  subtitle={timeOf(i)} tag="Impegno" tagClass="bg-sky-50 text-sky-800 border-sky-200" members={i.group || []}
                  details={(i.description || i.location) && (
                    <div className="text-sm text-stone-700 space-y-1">
                      {i.location && <div className="flex items-center gap-1.5 text-stone-600"><MapPin size={13}/> {i.location}</div>}
                      {i.description && <div className="whitespace-pre-line">{i.description}</div>}
                    </div>
                  )}/>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
